import { getGetPlatformCatalogsQueryKey, PlatformCatalogDTO, PublicationStatus } from "@bvhi/backend-api/apis";
import {
  getGetPlatformCatalogByIdQueryKey,
  useGetPlatformCatalogById,
  useUpdatePlatformCatalog,
} from "@bvhi/backend-api/apis/api/platform-catalog/platform-catalog";
import { Box, LoadingButton } from "@bvhi/ui-base";
import { yupResolver } from "@hookform/resolvers/yup";
import { ArrowRightIcon } from "@mui/x-date-pickers";
import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, object, string } from "yup";
import { BVHINavigationBackButton } from "../../../../../components/bvhi-navigation-back-button";
import { FormBlockerDialog } from "../../../../../components/form/form-blocker-dialog";
import { FormWithActionCard } from "../../../../../components/form/form-with-action-card";
import { NavigationBar } from "../../../../../components/navigation-bar";
import { useApiErrorContext } from "../../../../../context/use-api-error-context";
import { useAuthContext } from "../../../../../context/use-auth-context";
import { pick, pickIfSet } from "../../../../../helpers/object";
import { useToast } from "../../../../../hooks/use-toast";
import { Route } from "../../../../../routes/_main/app/catalogs.create";
import { toNarrowedTypeOrNull } from "../../../../../utils/ts";
import { CatalogsCreateCreateCatalogCard } from "./catalogs-create-create-catalog-card";
import { CatalogsCreateStepper } from "./catalogs-create-stepper";

interface FormValues {
  name: string;
  notes: string;
  published: boolean;
}

interface Props {
  steps: string[];
  activeStep: number;
}

export const CatalogsCreateUpdateCatalogView = ({ steps, activeStep }: Props) => {
  const queryClient = useQueryClient();
  const { currentUser } = useAuthContext();
  const { createdCatalogId } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { t } = useTranslation(["validation", "errors", "users", "common", "catalogs"]);
  const { setErrors } = useApiErrorContext();
  const { pushUpdateErrorToast, pushUpdateSuccessToast } = useToast();
  const { data: getPlatformCatalogByIdResponse, dataUpdatedAt: getPlatformDataByIdUpdatedAt } = useGetPlatformCatalogById(
    currentUser?.organisation?.id ?? "",
    createdCatalogId
  );
  const { mutate: updatePlatformCatalog, isPending: isUpdatePlatformCatalogPending } = useUpdatePlatformCatalog();

  const updateCatalogSchema: AnyObjectSchema = object({
    name: string().min(1, t("validation:string.min.1")).required(t("validation:required")),
    note: string().min(1, t("validation:string.min.1")).optional(),
  });

  const {
    formState: { isDirty, isValid, isSubmitSuccessful, ...otherFormState },
    reset,
    handleSubmit,
    ...otherMethods
  } = useForm<FormValues>({
    resolver: yupResolver(updateCatalogSchema),
    mode: "all",
  });

  const catalog = toNarrowedTypeOrNull<PlatformCatalogDTO>(getPlatformCatalogByIdResponse?.data, "id");

  useEffect(() => {
    if (catalog) {
      const selectedPlatformData = pick(catalog, ["name", "notes", "platformCatalogStatus"]);
      const platformCatalogStatus = pick(catalog, ["platformCatalogStatus"]).platformCatalogStatus;

      reset({
        ...selectedPlatformData,
        published: platformCatalogStatus === (PublicationStatus.DRAFT || PublicationStatus.DELETED) ? false : true,
      });
    }
  }, [catalog, getPlatformDataByIdUpdatedAt, reset]);

  const onSubmit: SubmitHandler<FormValues> = (data) => {
    if (!catalog) return;
    const oldValues = pick(catalog, ["id", "platformCatalogAccessType", "platformCatalogAccessMatchingBlocks"]);
    const values = pickIfSet(data, ["name", "notes"]);

    updatePlatformCatalog(
      {
        supplierId: currentUser?.organisation?.id ?? "",
        platformCatalogId: createdCatalogId,
        data: { ...oldValues, ...values, platformCatalogStatus: data.published === true ? "PUBLISHED" : "DRAFT" },
      },
      {
        onSuccess: () => {
          pushUpdateSuccessToast(t("catalogs:catalogs.catalog"));
          void navigate({ search: { createdCatalogId, step: activeStep + 1 }, replace: true });
        },
        onError: (error) => {
          if (error.status && [400, 403].includes(error.status)) {
            setErrors(error);
          }
          if (error.status === 500) pushUpdateErrorToast(t("catalogs:catalogs.catalog"));
        },
        onSettled: () => {
          void queryClient.invalidateQueries({ queryKey: getGetPlatformCatalogsQueryKey(currentUser?.organisation?.id ?? "") });
          void queryClient.invalidateQueries({
            queryKey: getGetPlatformCatalogByIdQueryKey(currentUser?.organisation?.id ?? "", createdCatalogId),
          });
        },
      }
    );
  };

  return (
    <FormProvider
      formState={{ isDirty, isValid, isSubmitSuccessful, ...otherFormState }}
      handleSubmit={handleSubmit}
      reset={reset}
      {...otherMethods}
    >
      <form onSubmit={(event) => void handleSubmit(onSubmit)(event)}>
        <>
          <NavigationBar
            actions={[<BVHINavigationBackButton key={0} fallbackNavigateOptions={{ to: "/app/catalogs" }} />]}
            showBreadcrumbs={true}
          />
          <Box py={4}>
            <CatalogsCreateStepper activeStep={activeStep} steps={steps} />
          </Box>
          <FormWithActionCard
            contained
            actionCardContent={
              <LoadingButton disabled={!isValid} loading={isUpdatePlatformCatalogPending} startIcon={<ArrowRightIcon />} type="submit">
                {t("common:button.next.step")}
              </LoadingButton>
            }
          >
            <CatalogsCreateCreateCatalogCard />
          </FormWithActionCard>
          <FormBlockerDialog isDirty={isDirty && !isSubmitSuccessful} />
        </>
      </form>
    </FormProvider>
  );
};
